import React, { useEffect, useState } from 'react';
import { Map, CustomOverlayMap } from 'react-kakao-maps-sdk';
import axiosInstance from '../api/axiosInstance'; // axiosInstance 임포트
import Modal from 'react-modal'; // react-modal 임포트
import './LocationDistributionContent.css';

const LocationDistributionContent = () => {
  const [currentMapCenter, setCurrentMapCenter] = useState({ lat: 37.4298, lng: 127.2551 }); // 동적 지도 중심
  const [currentMapLevel, setCurrentMapLevel] = useState(9); // 동적 지도 레벨
  const [currentMapBounds, setCurrentMapBounds] = useState(null); // 동적 지도 영역 제한

  // 데이터 관련 상태
  const [locationData, setLocationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTypeTab, setActiveTypeTab] = useState('unitPrice'); // 'unitPrice' (단가) 또는 'lumpSum' (총가)

  // 모달 관련 상태
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedDong, setSelectedDong] = useState(null);
  const [dongDetails, setDongDetails] = useState([]);
  const [allTasksData, setAllTasksData] = useState([]); // 모든 공사 데이터를 저장할 상태
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString()); // 기본값은 현재 연도

  // activeTypeTab이 변경될 때 selectedYear를 초기화
  useEffect(() => {
    if (activeTypeTab === 'unitPrice') {
      setSelectedYear(currentYear.toString());
    } else if (activeTypeTab === 'lumpSum') {
      setSelectedYear(currentYear.toString()); // 총가 탭의 기본 연도를 현재 연도로 설정
    }
  }, [activeTypeTab]);

  useEffect(() => {
    if (!window.kakao || !window.kakao.maps) {
      setError("카카오맵 API를 불러오지 못했습니다.");
      setLoading(false);
      return;
    }

    // 경기도 광주시의 대략적인 영역 정의 (남서쪽, 북동쪽 좌표)
    const gwangjuSwLatLng = new window.kakao.maps.LatLng(37.30, 127.10);
    const gwangjuNeLatLng = new window.kakao.maps.LatLng(37.50, 127.40);
    const gwangjuBounds = new window.kakao.maps.LatLngBounds(gwangjuSwLatLng, gwangjuNeLatLng);

    // 대한민국 중앙 좌표 및 전국 레벨
    const koreaCenter = { lat: 36.407757, lng: 127.766922 };

    if (activeTypeTab === 'unitPrice') {
      setCurrentMapCenter({ lat: 37.3998, lng: 127.2551 }); // 광주시 중심
      setCurrentMapLevel(9); // 광주시 레벨
      setCurrentMapBounds(gwangjuBounds); // 광주시 영역 제한
    } else if (activeTypeTab === 'lumpSum') {
      setCurrentMapCenter(koreaCenter); // 대한민국 중심
      setCurrentMapLevel(13); // 전국 레벨
      setCurrentMapBounds(null); // 영역 제한 없음
    }

    const geocoder = new window.kakao.maps.services.Geocoder();

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get('/tasks/location-distribution', {
          params: {
            type: activeTypeTab === 'unitPrice' ? '단가' : '총가',
            year: selectedYear // 항상 selectedYear를 전달
          }
        });
        const backendData = response.data; // This is already the list of {dong, count}

        // Fetch all tasks for summary based on selectedYear and activeTypeTab
        let allTasksUrl = '/tasks';
        const taskType = activeTypeTab === 'unitPrice' ? '단가' : '총가';

        if (selectedYear !== 'all') {
          allTasksUrl = `/tasks?year=${selectedYear}&type=${taskType}`;
        } else {
          allTasksUrl = `/tasks?type=${taskType}`;
        }
        const allTasksResponse = await axiosInstance.get(allTasksUrl);
        setAllTasksData(allTasksResponse.data);

        const promises = backendData.map(item =>
          new Promise((resolve, reject) => {
            const fullAddress = activeTypeTab === 'unitPrice' ? `경기도 광주시 ${item.dong}` : item.dong;
            geocoder.addressSearch(fullAddress, (result, status) => {
              if (status === window.kakao.maps.services.Status.OK) {
                resolve({
                  dong: item.dong,
                  count: item.count,
                  lat: result[0].y,
                  lng: result[0].x,
                });
              } else {
                console.warn(`Geocoding failed for ${fullAddress}`);
                resolve(null);
              }
            });
          })
        );

        const results = await Promise.all(promises);
        setLocationData(results.filter(item => item !== null));
      } catch (err) {
        setError("데이터를 불러오거나 주소 변환 중 오류가 발생했습니다.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activeTypeTab, selectedYear]); // activeTypeTab과 selectedYear이 변경될 때마다 useEffect 재실행

  const handleMarkerClick = async (dong) => {
    try {
      const response = await axiosInstance.get(`/tasks/dong/${dong}`);
      const filteredDetails = response.data.filter(task => {
        if (activeTypeTab === 'unitPrice') {
          return task.type === '단가';
        } else if (activeTypeTab === 'lumpSum') {
          return task.type === '총가';
        }
        return false;
      });
      setDongDetails(filteredDetails);
      setSelectedDong(dong);
      setModalIsOpen(true);
    } catch (error) {
      console.error("Failed to fetch dong details:", error);
      setError("공사 정보를 불러오는 데 실패했습니다.");
    }
  };

  const formatNumber = (number) => {
    if (number === null || number === undefined) return '0';
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  if (loading) {
    return <div className="tab-content">지도 데이터를 불러오는 중...</div>;
  }

  if (error) {
    return <div className="tab-content">오류: {error}</div>;
  }

  return (
    <div className="tab-content">
        <br/><br/>
        <h2 style={{ margin: '0 auto', textAlign: 'center' }}>
            {activeTypeTab === 'unitPrice' ? '경기도 광주시 공사 지역 분포' : '전국 총가 공사 지역 분포'}
        </h2>
        {activeTypeTab === 'unitPrice' && allTasksData.length > 0 && (() => {
            const unitPriceTasks = allTasksData.filter(task => task.type === '단가');
            const newTasks = unitPriceTasks.filter(task => task.constructionType === '신규').length;
            const repairTasks = unitPriceTasks.filter(task => task.constructionType === '보수').length;
            const operationTasks = unitPriceTasks.filter(task => task.constructionType === '운영').length;
            const undefindTasks = unitPriceTasks.filter(task => task.constructionType === null || task.constructionType === '미정').length;
            const totalTasks = unitPriceTasks.length;
            const inProgressTasks = unitPriceTasks.filter(task => task.taskStatus === '공사중').length;
            const completedTasks = unitPriceTasks.filter(task => task.taskStatus === '준공').length;

            return (
                <p style={{ textAlign: 'center', fontSize: '1.1em', color: '#555' }}>
                  {newTasks > 0 && (
                      <>
                        신규공사 <span style={{ fontWeight: 'bold', color: '#007bff' }}>{newTasks}건</span>,
                      </>
                  )}
                  {repairTasks > 0 && (
                      <>
                        보수공사 <span style={{ fontWeight: 'bold', color: '#007bff' }}>{repairTasks}건</span>,
                      </>
                  )}
                  {operationTasks > 0 && (
                      <>
                        운영공사 <span style={{ fontWeight: 'bold', color: '#007bff' }}>{operationTasks}건</span>,{'\u00A0'}
                      </>
                  )}
                    {undefindTasks > 0 && (
                        <>
                            {'\u00A0'}공사유형 미확인 <span style={{ fontWeight: 'bold', color: '#007bff' }}>{undefindTasks}건</span>{'\u00A0'}
                        </>
                    )}
                    {'\u00A0'}총 <span style={{ fontWeight: 'bold', color: '#ffc107' }}>{totalTasks}건</span>{'\u00A0'}의 공사 중{'\u00A0'}
                    <span style={{ fontWeight: 'bold', color: '#dc3545' }}>{inProgressTasks}건</span>{'\u00A0'}의 공사가 {'\u00A0'} 
                    <span style={{ fontWeight: 'bold', color: '#dc3545' }}>중지/재착공</span> 상태 이며{'\u00A0'} 
                    <span style={{ fontWeight: 'bold', color: '#28a745' }}>{completedTasks}건</span>{'\u00A0'}의 공사가 <span style={{ fontWeight: 'bold', color: '#28a745' }}>준공</span> 되었습니다.
                </p>
            );
        })()}

        {activeTypeTab === 'lumpSum' && allTasksData.length > 0 && (() => {
            const unitPriceTasks = allTasksData.filter(task => task.type === '총가');
            const newTasks = unitPriceTasks.filter(task => task.constructionType === '신규').length;
            const repairTasks = unitPriceTasks.filter(task => task.constructionType === '보수').length;
            const operationTasks = unitPriceTasks.filter(task => task.constructionType === '운영').length;
            const undefindTasks = unitPriceTasks.filter(task => task.constructionType === null || task.constructionType === '미정').length;
            const totalTasks = unitPriceTasks.length;
            const inProgressTasks = unitPriceTasks.filter(task => task.taskStatus === '공사중').length;
            const completedTasks = unitPriceTasks.filter(task => task.taskStatus === '준공').length;

            return (
                <p style={{ textAlign: 'center', fontSize: '1.1em', color: '#555' }}>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'red' }}>* 총가의 금액 및 건수는 선택한 연도까지의 전체 데이터를 불러옵니다.</p>
                  <br/>
                  {newTasks > 0 && (
                      <>
                        신규공사 <span style={{ fontWeight: 'bold', color: '#007bff' }}>{newTasks}건</span>,
                      </>
                  )}
                  {repairTasks > 0 && (
                      <>
                        보수공사 <span style={{ fontWeight: 'bold', color: '#007bff' }}>{repairTasks}건</span>,
                      </>
                  )}
                  {operationTasks > 0 && (
                      <>
                        운영공사 <span style={{ fontWeight: 'bold', color: '#007bff' }}>{operationTasks}건</span>,{'\u00A0'}
                      </>
                  )}
                    {undefindTasks > 0 && (
                        <>
                            {'\u00A0'}공사유형 미확인 <span style={{ fontWeight: 'bold', color: '#007bff' }}>{undefindTasks}건</span>{'\u00A0'}
                        </>
                    )}
                    {'\u00A0'}총 <span style={{ fontWeight: 'bold', color: '#ffc107' }}>{totalTasks}건</span>{'\u00A0'}의 공사 중{'\u00A0'}
                    <span style={{ fontWeight: 'bold', color: '#dc3545' }}>{inProgressTasks}건</span>{'\u00A0'}의 공사가 {'\u00A0'} 
                    <span style={{ fontWeight: 'bold', color: '#dc3545' }}>중지/재착공</span> 상태 이며{'\u00A0'} 
                    <span style={{ fontWeight: 'bold', color: '#28a745' }}>{completedTasks}건</span>{'\u00A0'}의 공사가 <span style={{ fontWeight: 'bold', color: '#28a745' }}>준공</span> 되었습니다.
                </p>
            );
        })()}
        <br/>
        <div className="tabs" style={{ justifyContent: 'center', marginBottom: '20px' }}>
            <button
                className={`tab-button ${activeTypeTab === 'unitPrice' ? 'active' : ''}`}
                onClick={() => setActiveTypeTab('unitPrice')}
            >
                단가
            </button>
            <button
                className={`tab-button ${activeTypeTab === 'lumpSum' ? 'active' : ''}`}
                onClick={() => setActiveTypeTab('lumpSum')}
            >
                총가
            </button>
            <select
                className="year-selector"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
            >
                {Array.from({ length: 31 }, (_, i) => currentYear - 15 + i).map(year => (
                    <option key={year} value={year.toString()}>
                        {year}
                    </option>
                ))}
            </select>
        </div>
        <Map
            center={currentMapCenter}
            style={{ width: '80%', height: '500px', margin: '0 auto', borderRadius: '10px' }}
            level={currentMapLevel}
            isZoomable={false}
            isDraggable={false}
            scrollwheel={false}
            bounds={currentMapBounds}
        >
            {locationData.map((data, index) => (
            <CustomOverlayMap
                key={index}
                position={{ lat: data.lat, lng: data.lng }}
                yAnchor={1}
            >
                <div className="custom-overlay-container" onClick={() => handleMarkerClick(data.dong)}>
                <div className="label">{data.dong}</div>
                <div className="count">{data.count}건</div>
                </div>
            </CustomOverlayMap>
            ))}
        </Map>
        <Modal
            isOpen={modalIsOpen}
            onRequestClose={() => setModalIsOpen(false)}
            contentLabel="Dong Details"
            style={{
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1000
                },
                content: {
                    top: '50%',
                    left: '50%',
                    right: 'auto',
                    bottom: 'auto',
                    marginRight: '-50%',
                    transform: 'translate(-50%, -50%)',
                    width: '800px',
                    height: '600px',
                    zIndex: 1001
                }
            }}
        >
            <div className="modal-header">
                <h2>{selectedDong} 공사 정보</h2>
            </div>
            <div className="modal-body">
                <div className="list-header">
                    <div className="list-item list-item-name">공사명</div>
                    <div className="list-item">공사번호(감리)</div>
                    <div className="list-item">계약금액</div>
                    <div className="list-item">준공금액(기성)</div>
                    <div className="list-item">잔액</div>
                    <div className="list-item">진행현황</div>
                    <div className="list-item">공사유형</div>
                </div>
                <div className="list-body">
                    {dongDetails.map(task => (
                        <div className="list-row" key={task.id}>
                            <div className="list-item list-item-name">{task.name}</div>
                            <div className="list-item">{task.number}</div>
                            <div className="list-item">{formatNumber(task.initialContractAmount)}</div>
                            <div className="list-item">{formatNumber(task.completionAmount)}</div>
                            <div className="list-item">{formatNumber(task.initialContractAmount - task.completionAmount)}</div>
                            <div className="list-item">{task.taskStatus}</div>
                            <div className="list-item">{task.constructionType === null ? '미정' : task.constructionType === '미정' ? '미정' : task.constructionType}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="modal-footer">
                <button onClick={() => setModalIsOpen(false)}>닫기</button>
            </div>
        </Modal>
        
    </div>
  );
};

export default LocationDistributionContent;
